import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './PrintTasksPreview.module.scss';
import { Task } from '@spike/task-model';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PrintTasksTable } from '../PrintTasksTable/PrintTasksTable';
import { mapTasks } from '../utils/TaskPrintMapper';
import { groupTasksByRoomUnit, roomGroupTasks } from '../../../../Dashboard/grouping';
import { useMasterData, useTimeZone } from 'hooks';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Spinner from 'components/UI/V3/Spinner';

export interface PrintTasksPreviewProps {
    tasks: Task[];
    date: string;
    category?: string;
    onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
    feeding: 'Feeding',
    medication: 'Meds',
    care: 'Tasks'
};

function usePrint(onClose: () => void, isReadyToPrint: boolean) {
    useHideHubSpotChat(true);

    useEffect(() => {
        if (!isReadyToPrint) return;

        document.body.classList.add('printing');

        const timer = setTimeout(() => window.print(), 50);

        const handleAfterPrint = () => {
            document.body.classList.remove('printing');
            onClose();
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
            document.body.classList.remove('printing');
        };
    }, [onClose, isReadyToPrint]);

    useHideHubSpotChat(false);
}

const taskCountLabel = (count: number) => `${count} ${count === 1 ? 'Task' : 'Tasks'}`;

export const PrintTasksPreview: React.FC<PrintTasksPreviewProps> = ({ tasks, date, category, onClose }) => {
    const [isReadyToPrint, setIsReadyToPrint] = useState(false);

    usePrint(onClose, isReadyToPrint);

    const masterData = useMasterData();
    const timezone = useTimeZone();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const showLocationHeaders = organizationMarketplaces.length >= 2;
    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);

    const marketplaceNamesById = useMemo(
        () => new Map(organizationMarketplaces.map(marketplace => [marketplace.id, marketplace.name] as const)),
        [organizationMarketplaces]
    );

    const roomGroups = useMemo(() => groupTasksByRoomUnit(tasks, rooms.page), [tasks, rooms.page]);

    const header = (
        <div className={styles.printHeader}>
            <span className={styles.title}>{CATEGORY_LABELS[category ?? ''] ?? 'Tasks'}</span>
            <span className={styles.date}> · {date} · All Times</span>
        </div>
    );

    const unitTable = (unitTasks: Task[]) => (
        <PrintTasksTable
            groups={[{ locationName: '', rows: mapTasks(unitTasks, masterData, timezone, marketplaceNamesById) }]}
            showLocationHeaders={false}
        />
    );

    const content = (
        <div className={styles.printContent}>
            {roomGroups.map(room => {
                const locationName = showLocationHeaders
                    ? marketplaceNamesById.get(roomGroupTasks(room)[0]?.marketplaceId ?? -1)
                    : undefined;

                return (
                    <div
                        className={styles.roomBlock}
                        key={room.key}
                    >
                        <div className={styles.roomHeader}>
                            <span className={styles.roomName}>{room.roomName}</span>
                            {locationName && (
                                <span className={styles.roomLocation}>
                                    <FontAwesomeIcon icon={faLocationDot} /> {locationName}
                                </span>
                            )}
                            <span className={styles.roomCount}>{taskCountLabel(room.count)}</span>
                        </div>

                        {room.units.length === 0
                            ? unitTable(room.tasks)
                            : room.units.map(unit => (
                                  <React.Fragment key={unit.key}>
                                      <div className={styles.unitHeader}>
                                          <span className={styles.unitName}>{unit.unitName}</span>
                                          <span className={styles.unitCount}>{taskCountLabel(unit.tasks.length)}</span>
                                      </div>
                                      {unitTable(unit.tasks)}
                                  </React.Fragment>
                              ))}
                    </div>
                );
            })}
        </div>
    );

    const footer = (
        <div className={styles.printFooter}>
            <img
                className={styles.footerLogo}
                src="/images/logo/poweredByCuddles.png"
                onLoad={() => setIsReadyToPrint(true)}
            />
        </div>
    );

    return ReactDOM.createPortal(
        <div className={styles.printOverlay}>
            {!isReadyToPrint ? (
                <div className={styles.spinnerOverlay}>
                    <Spinner />
                    {footer}
                </div>
            ) : (
                <>
                    {header}
                    {content}
                    {footer}
                </>
            )}
        </div>,
        document.body
    );
};

export default PrintTasksPreview;
